import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Dependency-free SEO head manager.
 * Sets title, meta description, canonical, OpenGraph + Twitter tags, hreflang alternates,
 * and JSON-LD structured data per route. All tags are created/updated idempotently and
 * cleaned up on unmount so routes don't leak stale metadata.
 *
 * `og:locale` and hreflang alternates follow the active i18n language (EN/HI/MR); the effect
 * re-runs on language change so metadata stays in sync with the rendered language.
 *
 * Why not react-helmet-async? It is an extra dependency for something the DOM
 * already supports directly. This hook is robust, SSR-safe-ish, and zero-cost.
 */

interface SeoProps {
  title: string
  description?: string
  canonicalPath?: string
  ogType?: 'website' | 'article'
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /** Breadcrumb items: [{ name, path }] — rendered as BreadcrumbList schema */
  breadcrumbs?: { name: string; path: string }[]
  noIndex?: boolean
}

const SITE_NAME = 'TB Mukt Bharat'
const SITE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://tbmuktbharat.org'

/** Supported languages → OpenGraph locale codes. */
const OG_LOCALE: Record<string, string> = {
  en: 'en_IN',
  hi: 'hi_IN',
  mr: 'mr_IN',
}
/** All languages we publish hreflang alternates for (same URL, different hreflang). */
const HREFLANGS = ['en-IN', 'hi-IN', 'mr-IN'] as const

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  if (!href) return
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/** Upsert a <link rel="alternate" hreflang="…"> — keyed by hreflang so multiple coexist. */
function upsertHreflang(hreflang: string, href: string) {
  if (!href) return
  let el = document.head.querySelector(
    `link[rel="alternate"][hreflang="${hreflang}"]`
  ) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function upsertJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  const el = document.createElement('script')
  el.type = 'application/ld+json'
  el.setAttribute('data-seo-jsonld', 'true')
  el.text = JSON.stringify(data)
  document.head.appendChild(el)
}

export function Seo({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  jsonLd,
  breadcrumbs,
  noIndex,
}: SeoProps) {
  const { i18n } = useTranslation()
  const language = i18n.language

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`
    document.title = fullTitle

    const desc = description || 'TB Mukt Bharat — India’s TB elimination mission. Find TB information, treatment support, TB Supporter (Ni-kshay Mitra) registration, volunteer opportunities, and official resources.'
    const canonical = canonicalPath ? `${SITE_ORIGIN}${canonicalPath}` : window.location.href

    // Active language → base code (e.g. "en-US" → "en") → OG locale, defaulting to en_IN.
    const baseLang = (language || 'en').split('-')[0]
    const ogLocale = OG_LOCALE[baseLang] ?? 'en_IN'

    upsertMeta('name', 'description', desc)
    upsertLink('canonical', canonical)

    // OpenGraph
    upsertMeta('property', 'og:title', fullTitle)
    upsertMeta('property', 'og:description', desc)
    upsertMeta('property', 'og:type', ogType)
    upsertMeta('property', 'og:url', canonical)
    upsertMeta('property', 'og:site_name', SITE_NAME)
    upsertMeta('property', 'og:locale', ogLocale)

    // hreflang alternates — all languages live at the same URL (client-side switcher),
    // so each alternate points to the canonical URL with its own hreflang. x-default → en.
    HREFLANGS.forEach((hl) => upsertHreflang(hl, canonical))
    upsertHreflang('x-default', canonical)

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', fullTitle)
    upsertMeta('name', 'twitter:description', desc)

    if (noIndex) {
      upsertMeta('name', 'robots', 'noindex, nofollow')
    }

    // Structured data
    const blocks: Record<string, unknown>[] = []
    if (breadcrumbs && breadcrumbs.length) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${SITE_ORIGIN}${b.path}`,
        })),
      })
    }
    if (jsonLd) {
      if (Array.isArray(jsonLd)) blocks.push(...jsonLd)
      else blocks.push(jsonLd)
    }
    blocks.forEach(upsertJsonLd)

    return () => {
      // Clean up JSON-LD blocks we added (leave static ones alone)
      document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove())
      if (noIndex) {
        const r = document.head.querySelector('meta[name="robots"]')
        if (r) r.remove()
      }
    }
  }, [title, description, canonicalPath, ogType, jsonLd, breadcrumbs, noIndex, language])

  return null
}